/**
 * Web Push subscription helpers.
 *
 * The browser owns the actual `PushSubscription` object — we just shuttle it
 * to/from the backend. Public surface:
 *
 *   isPushSupported()          → feature detection
 *   getCurrentPermission()     → 'granted' | 'denied' | 'default'
 *   isSubscribed()             → has an active SW subscription
 *   subscribePush()            → request permission, subscribe, POST to backend
 *   unsubscribePush()          → unsubscribe + DELETE on backend
 *
 * Backend may return 503 from /push/vapid-public-key when VAPID env vars are
 * missing — UI should gracefully hide the CTA in that case (see canUsePush).
 */

import { apiFetch } from '../api/core'

const VAPID_KEY_PATH = '/push/vapid-public-key'
const SUBSCRIBE_PATH = '/push/subscribe'

/** Browser supports the full SW + Push stack required to subscribe. */
export function isPushSupported() {
    if (typeof window === 'undefined') return false
    return (
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        'Notification' in window
    )
}

export function getCurrentPermission() {
    if (typeof Notification === 'undefined') return 'denied'
    return Notification.permission
}

/** True iff the SW currently owns an active push subscription. */
export async function isSubscribed() {
    if (!isPushSupported()) return false
    try {
        const reg = await navigator.serviceWorker.ready
        const sub = await reg.pushManager.getSubscription()
        return !!sub
    } catch {
        return false
    }
}

/** True iff the backend has VAPID keys configured (probes /push/vapid-public-key). */
export async function canUsePush() {
    if (!isPushSupported()) return false
    try {
        await apiFetch(VAPID_KEY_PATH, { cache: 'no-store' })
        return true
    } catch {
        return false
    }
}

function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
    const rawData = atob(base64)
    const out = new Uint8Array(rawData.length)
    for (let i = 0; i < rawData.length; i++) out[i] = rawData.charCodeAt(i)
    return out
}

function arrayBufferToBase64Url(buffer) {
    const bytes = new Uint8Array(buffer)
    let binary = ''
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * Full subscription handshake.
 *
 * 1. Ask user permission (`Notification.requestPermission`).
 *    Promise resolves to current permission — 'granted' | 'denied' | 'default'.
 *    On non-granted result: throw with explanatory code.
 * 2. Fetch VAPID public key from backend; throw if not configured.
 * 3. Convert base64url → Uint8Array for `applicationServerKey`.
 * 4. `pushManager.subscribe({ userVisibleOnly: true })` — userVisibleOnly is
 *    required by Chromium browsers; without it subscribe rejects.
 * 5. POST {endpoint, keys} to backend.
 *
 * Returns the PushSubscription object. Throws Error with `.code` for UI to
 * surface (`permission-denied`, `vapid-unavailable`, `subscribe-failed`).
 */
export async function subscribePush() {
    if (!isPushSupported()) {
        const err = new Error('push-not-supported')
        err.code = 'push-not-supported'
        throw err
    }

    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
        const err = new Error('permission-denied')
        err.code = 'permission-denied'
        throw err
    }

    let vapid
    try {
        vapid = await apiFetch(VAPID_KEY_PATH, { method: 'GET' })
    } catch (e) {
        const err = new Error('vapid-unavailable')
        err.code = 'vapid-unavailable'
        err.cause = e
        throw err
    }
    if (!vapid?.publicKey) {
        const err = new Error('vapid-unavailable')
        err.code = 'vapid-unavailable'
        throw err
    }

    const reg = await navigator.serviceWorker.ready
    let sub
    try {
        sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapid.publicKey),
        })
    } catch (e) {
        const err = new Error('subscribe-failed')
        err.code = 'subscribe-failed'
        err.cause = e
        throw err
    }

    const payload = {
        endpoint: sub.endpoint,
        keys: {
            p256dh: arrayBufferToBase64Url(sub.getKey('p256dh')),
            auth: arrayBufferToBase64Url(sub.getKey('auth')),
        },
    }
    await apiFetch(SUBSCRIBE_PATH, {
        method: 'POST',
        body: JSON.stringify(payload),
    })

    return sub
}

/** Unsubscribe locally and tell backend to forget this endpoint. */
export async function unsubscribePush() {
    if (!isPushSupported()) return false
    const reg = await navigator.serviceWorker.ready
    const sub = await reg.pushManager.getSubscription()
    if (!sub) return false

    const endpoint = sub.endpoint
    try { await sub.unsubscribe() } catch { /* best effort */ }
    try {
        await apiFetch(SUBSCRIBE_PATH, {
            method: 'DELETE',
            body: JSON.stringify({ endpoint }),
        })
    } catch { /* server already may have purged via 410 */ }
    return true
}
